import React, { useState, useMemo } from "react";
import styled from "styled-components";

const TableContainer = styled.div`
  width: 100%;
  margin-top: 1rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const TableHeader = styled.th<{ $sortable?: boolean }>`
  background: #013566;
  color: white;
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  cursor: ${(props) => (props.$sortable ? "pointer" : "default")};
  user-select: none;
  transition: background-color 0.2s ease;

  &:hover {
    background: ${(props) => (props.$sortable ? "#012a4a" : "#013566")};
  }

  &:after {
    content: ${(props) => (props.$sortable ? '"↕"' : '""')};
    margin-left: 8px;
    opacity: 0.7;
  }
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #eee;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 12px 16px;
  border-bottom: 1px solid #eee;
`;

const StateName = styled.span`
  font-weight: 500;
  color: #013566;
`;

const ViewCount = styled.span<{ $intensity: number }>`
  font-weight: 600;
  color: ${(props) => {
    if (props.$intensity === 0) return "#6c757d";
    if (props.$intensity <= 10) return "#28a745";
    if (props.$intensity <= 25) return "#20c997";
    if (props.$intensity <= 50) return "#17a2b8";
    if (props.$intensity <= 100) return "#007bff";
    if (props.$intensity <= 150) return "#6f42c1";
    if (props.$intensity <= 200) return "#e83e8c";
    return "#dc3545";
  }};
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  margin-bottom: 1rem;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #013566;
    box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
  }
`;

const StatsBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #eee;
`;

const StatItem = styled.div`
  text-align: center;

  .label {
    font-size: 12px;
    color: #666;
    margin-bottom: 4px;
  }

  .value {
    font-size: 16px;
    font-weight: 600;
    color: #013566;
  }
`;

interface StateTableProps {
  stateData: Record<string, number>;
  dataType?: "views" | "submissions" | "clicks";
}

type SortField = "state" | "views";
type SortDirection = "asc" | "desc";

const StateTable: React.FC<StateTableProps> = ({
  stateData,
  dataType = "views",
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<SortField>("views");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");

  // State abbreviations mapping
  const stateAbbreviations: Record<string, string> = {
    AL: "Alabama",
    AK: "Alaska",
    AZ: "Arizona",
    AR: "Arkansas",
    CA: "California",
    CO: "Colorado",
    CT: "Connecticut",
    DE: "Delaware",
    FL: "Florida",
    GA: "Georgia",
    HI: "Hawaii",
    ID: "Idaho",
    IL: "Illinois",
    IN: "Indiana",
    IA: "Iowa",
    KS: "Kansas",
    KY: "Kentucky",
    LA: "Louisiana",
    ME: "Maine",
    MD: "Maryland",
    MA: "Massachusetts",
    MI: "Michigan",
    MN: "Minnesota",
    MS: "Mississippi",
    MO: "Missouri",
    MT: "Montana",
    NE: "Nebraska",
    NV: "Nevada",
    NH: "New Hampshire",
    NJ: "New Jersey",
    NM: "New Mexico",
    NY: "New York",
    NC: "North Carolina",
    ND: "North Dakota",
    OH: "Ohio",
    OK: "Oklahoma",
    OR: "Oregon",
    PA: "Pennsylvania",
    RI: "Rhode Island",
    SC: "South Carolina",
    SD: "South Dakota",
    TN: "Tennessee",
    TX: "Texas",
    UT: "Utah",
    VT: "Vermont",
    VA: "Virginia",
    WA: "Washington",
    WV: "West Virginia",
    WI: "Wisconsin",
    WY: "Wyoming",
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("desc");
    }
  };

  const filteredAndSortedData = useMemo(() => {
    // Convert to array and filter
    let data = Object.entries(stateData)
      .filter(([stateCode, views]) => {
        const stateName = stateAbbreviations[stateCode] || stateCode;
        return (
          stateName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          stateCode.toLowerCase().includes(searchTerm.toLowerCase())
        );
      })
      .map(([stateCode, views]) => ({
        stateCode,
        stateName: stateAbbreviations[stateCode] || stateCode,
        views,
      }));

    // Sort
    data.sort((a, b) => {
      let comparison = 0;
      if (sortField === "state") {
        comparison = a.stateName.localeCompare(b.stateName);
      } else {
        comparison = a.views - b.views;
      }
      return sortDirection === "asc" ? comparison : -comparison;
    });

    return data;
  }, [stateData, searchTerm, sortField, sortDirection]);

  const totalCount = Object.values(stateData).reduce(
    (sum, value) => sum + value,
    0
  );
  const maxCount = Math.max(...Object.values(stateData));
  const statesWithData = Object.values(stateData).filter(
    (value) => value > 0
  ).length;

  const getDataTypeLabel = () => {
    switch (dataType) {
      case "submissions":
        return "Submissions";
      case "clicks":
        return "Clicks";
      default:
        return "Views";
    }
  };

  const getStatsLabel = () => {
    switch (dataType) {
      case "submissions":
        return "States with Submissions";
      case "clicks":
        return "States with Clicks";
      default:
        return "States with Views";
    }
  };

  return (
    <TableContainer>
      <StatsBar>
        <StatItem>
          <div className="label">Total States</div>
          <div className="value">{Object.keys(stateData).length}</div>
        </StatItem>
        <StatItem>
          <div className="label">{getStatsLabel()}</div>
          <div className="value">{statesWithData}</div>
        </StatItem>
        <StatItem>
          <div className="label">Total {getDataTypeLabel()}</div>
          <div className="value">{totalCount.toLocaleString()}</div>
        </StatItem>
        <StatItem>
          <div className="label">Highest State</div>
          <div className="value">{maxCount.toLocaleString()}</div>
        </StatItem>
      </StatsBar>

      <SearchInput
        type="text"
        placeholder="Search states..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      <Table>
        <thead>
          <tr>
            <TableHeader $sortable onClick={() => handleSort("state")}>
              State
            </TableHeader>
            <TableHeader $sortable onClick={() => handleSort("views")}>
              {getDataTypeLabel()}
            </TableHeader>
          </tr>
        </thead>
        <tbody>
          {filteredAndSortedData.map(({ stateCode, stateName, views }) => (
            <TableRow key={stateCode}>
              <TableCell>
                <StateName>{stateName}</StateName>
                <span
                  style={{ color: "#666", fontSize: "12px", marginLeft: "8px" }}
                >
                  ({stateCode})
                </span>
              </TableCell>
              <TableCell>
                <ViewCount $intensity={views}>
                  {views.toLocaleString()}
                </ViewCount>
              </TableCell>
            </TableRow>
          ))}
        </tbody>
      </Table>
    </TableContainer>
  );
};

export default StateTable;
